import React, { useState } from 'react';
import { FiTag, FiPlus, FiCheck, FiX } from 'react-icons/fi';
import { showToast } from './NotificationTray';

export default function TagSelectorModal({
  selectedImages,
  onClose,
  onTagSelected,
  availableTags,
  onTagsUpdated
}) {
  const [isCreating, setIsCreating] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#304849');
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const colorPresets = [
    '#ef4444', '#f97316', '#f59e0b', '#eab308',
    '#84cc16', '#22c55e', '#10b981', '#14b8a6',
    '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
    '#8b5cf6', '#a855f7', '#d946ef', '#ec4899',
    '#f43f5e', '#4a5f5d',
  ];

  async function createAndApplyTag() {
    if (!newTagName.trim() || saving) return;

    setSaving(true);
    try {
      const res = await fetch('/api/tags', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          label: newTagName.trim()
        }),
        signal: AbortSignal.timeout(10000)
      });

      if (res.ok) {
        const newTag = await res.json();
        newTag.color = newTagColor;
        const updatedTags = [...availableTags, newTag];
        onTagsUpdated(updatedTags);
        await onTagSelected(newTag.id);
        setNewTagName('');
        setNewTagColor('#304849');
        setIsCreating(false);
      }
    } catch (error) {
      console.error('Failed to create tag:', error);
      showToast('Failed to create tag', '', 'error');
    } finally {
      setSaving(false);
    }
  }

  const filteredTags = availableTags.filter(tag =>
    (tag.label || tag.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content tag-selector-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add Tag to {selectedImages.length} {selectedImages.length === 1 ? 'Photo' : 'Photos'}</h3>
          <button className="modal-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body tag-selector-body">
          {!isCreating ? (
            <>
              <div className="tag-selector-search-row">
                <div className="tag-search-wrapper">
                  <FiTag size={14} className="tag-search-icon" />
                  <input
                    type="text"
                    placeholder="Search tags..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="tag-search-input"
                    autoFocus
                  />
                </div>
                <button
                  className="tag-new-btn"
                  onClick={() => setIsCreating(true)}
                >
                  <FiPlus size={14} /> New Tag
                </button>
              </div>

              {filteredTags.length > 0 ? (
                <div className="tag-selector-list">
                  {filteredTags.map(tag => (
                    <button
                      key={tag.id}
                      className="tag-selector-pill"
                      onClick={() => onTagSelected(tag.id)}
                    >
                      <span
                        className="tag-pill-dot"
                        style={{ backgroundColor: tag.color || '#304849' }}
                      />
                      {tag.label || tag.name}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="tag-selector-empty">
                  <FiTag size={32} strokeWidth={1.5} />
                  <p>
                    {searchQuery ? 'No tags match your search' : 'No tags yet'}
                  </p>
                  <button className="tag-new-btn" onClick={() => setIsCreating(true)}>
                    <FiPlus size={14} /> Create your first tag
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="tag-creator-form">
              <input
                type="text"
                placeholder="Tag name..."
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                className="tag-name-input"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') createAndApplyTag();
                  if (e.key === 'Escape') {
                    setIsCreating(false);
                    setNewTagName('');
                  }
                }}
              />

              <div className="tag-color-grid">
                {colorPresets.map(color => (
                  <button
                    key={color}
                    className={`tag-color-swatch ${newTagColor === color ? 'selected' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewTagColor(color)}
                    aria-label={`Select color ${color}`}
                  />
                ))}
              </div>

              <div className="tag-creator-actions">
                <button
                  className="tag-cancel-btn"
                  onClick={() => {
                    setIsCreating(false);
                    setNewTagName('');
                    setNewTagColor('#304849');
                  }}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="tag-create-btn"
                  onClick={createAndApplyTag}
                  disabled={!newTagName.trim() || saving}
                >
                  <FiCheck size={14} /> {saving ? 'Creating...' : 'Create & Apply'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
